import React from 'react';
import { Box, Typography, Link } from '@mui/material';

/** Shared Terms of Service body content - used by Terms page and TermsAgreementDialog */
export const TermsContent = () => (
  <Box>
    <Typography variant="body1" paragraph>
      These Terms of Service (&quot;Terms&quot;) govern your use of the Workflow.Estate platform
      (&quot;Platform&quot;), operated by CodePulse Pty Ltd (&quot;we&quot;, &quot;us&quot;,
      &quot;our&quot;). By registering for an account or using our services, you agree to these Terms.
    </Typography>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        1. Acceptance of Terms
      </Typography>
      <Typography variant="body1" paragraph>
        By creating an agency or individual account, you confirm that you have read, understood, and
        agree to be bound by these Terms of Service and our Privacy Policy.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        2. Description of Service
      </Typography>
      <Typography variant="body1" paragraph>
        Workflow.Estate provides a real estate workflow management platform for agencies and agents
        to manage properties, vendors, open-home attendees, buyer enquiries, contractors, and
        related workflows.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        3. Account and Registration
      </Typography>
      <Typography variant="body1" paragraph>
        You must provide accurate and complete information when registering. You are responsible
        for maintaining the confidentiality of your account credentials and for all activities
        under your account.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        4. Acceptable Use
      </Typography>
      <Typography variant="body1" paragraph>
        You agree to use the Platform only for lawful purposes and in accordance with applicable
        laws. You must not use the Platform to transmit harmful, offensive, or illegal content, or
        to engage in activities that could harm the Platform or other users.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        5. Service Availability and Disruptions
      </Typography>
      <Typography variant="body1" paragraph>
        We strive to keep the Platform available and reliable, but we do not guarantee
        uninterrupted or error-free access. The service may be temporarily unavailable due to
        planned maintenance, updates, technical issues, or other reasons. We will use reasonable
        efforts to minimise disruption and to notify users of significant planned outages where
        practicable. You should not rely solely on the Platform for time-critical business
        operations and should maintain appropriate backups of your data.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        6. Subscription and Payment
      </Typography>
      <Typography variant="body1" paragraph>
        Subscription plans, trials, and payment terms are described in the Plans section of the
        Platform. By subscribing, you agree to the applicable pricing and billing terms.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        7. Termination
      </Typography>
      <Typography variant="body1" paragraph>
        We may suspend or terminate your account if you breach these Terms. You may request
        account deletion at any time through the account settings.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        8. Force Majeure
      </Typography>
      <Typography variant="body1" paragraph>
        We shall not be liable for any failure or delay in performing our obligations where such
        failure or delay results from circumstances beyond our reasonable control. Such
        circumstances include, but are not limited to: natural disasters (earthquakes, floods,
        fires, storms); war, terrorism, or civil unrest; pandemic or epidemic; government actions,
        embargoes, or sanctions; failure or outage of third-party services (including cloud
        providers, payment processors, telecommunications, or DNS); cyber attacks, security
        incidents, or denial-of-service; power outages or infrastructure failures; labour disputes;
        or any other event that could not reasonably have been foreseen or prevented.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        9. Limitation of Liability
      </Typography>
      <Typography variant="body1" paragraph>
        To the maximum extent permitted by law, we shall not be liable for any indirect, incidental,
        special, or consequential damages arising from your use of the Platform.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        10. Changes
      </Typography>
      <Typography variant="body1" paragraph>
        We may update these Terms from time to time. Continued use of the Platform after changes
        constitutes acceptance of the revised Terms.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        11. Contact
      </Typography>
      <Typography variant="body1" paragraph>
        For questions about these Terms, contact us at{' '}
        <Link href="mailto:support@workflow.estate" underline="hover">
          support@workflow.estate
        </Link>
        .
      </Typography>
    </Box>
  </Box>
);
